import { IP_GUARD_MSG, codeFromText, normalizeCode, type CodeChallenge, type CodeResendResponse } from "@genbaryoku/contract";
import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent } from "react";
import { ApiError, messageOf, setCodePrompt } from "../api.js";
import { ErrorText, Field, Help, guardComposingEnter } from "./ui.jsx";

type Actions = { submit: (code: string) => Promise<unknown>; resend: () => Promise<CodeResendResponse> };
type Open = { ch: CodeChallenge; actions: Actions; resolve: (v: unknown) => void; reject: (e: unknown) => void };

/** 窓のなかで直せる間違い（窓を閉じずにもう一度打ってもらう） */
const RETRY_HERE = new Set(["AUTH_CODE_WRONG", "AUTH_CODE_FORMAT", "AUTH_CODE_EXPIRED"]);

/**
 * 確認コードの窓（#110、docs/42）。アプリの一番外に1つだけ置く（app.tsx）。
 * api.ts が 403 AUTH_CODE_REQUIRED を受けるとここを開き、コードを付けて同じ操作を送り直す。
 * どの画面（ログイン・会員登録・個人設定・振込口座、これからメールアドレスの変更・カードの登録）でも同じ窓
 *
 * 打つ手間を減らす（#111、Figma「③ メールのデザイン」の iPhone の見本）：
 *   ・欄は autocomplete="one-time-code"（iPhone・Mac の Safari＋「メール」アプリならキーボードの上にコードが出る）
 *   ・欄の中の右端に「貼り付け」：コピーしてきた文（「確認コード：482913」の行ごとでも）から6桁を拾って入れる
 *   ・欄に貼り付けても同じく6桁を拾う
 *   ・6桁そろったら自動で確認（「確認」を押さなくてよい）。日本語入力の変換中は待つ
 */
export function CodeDialogHost() {
  const [open, setOpen] = useState<Open | null>(null);
  useEffect(() => {
    setCodePrompt((ch, actions) => new Promise((resolve, reject) => setOpen({ ch, actions, resolve, reject })));
    return () => setCodePrompt(null);
  }, []);
  if (!open) return null;
  return <CodeDialog key={open.ch.challengeToken} {...open} onClose={() => setOpen(null)} />;
}

function CodeDialog({ ch, actions, resolve, reject, onClose }: Open & { onClose: () => void }) {
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState(ch.sentTo);
  const [err, setErr] = useState<string | undefined>();
  const [note, setNote] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);

  const finish = (fn: () => void) => {
    onClose();
    fn();
  };
  const cancel = () =>
    finish(() => reject(new ApiError(400, { type: "about:blank", status: 400, title: IP_GUARD_MSG.codeCancelled, code: "AUTH_CODE_CANCELLED" })));

  const canPaste = typeof navigator !== "undefined" && typeof navigator.clipboard?.readText === "function";

  // 送っている途中（state は描き直すまで古いので ref で見る。変換の確定と input が続けて来ても2回送らない）
  const inflight = useRef(false);

  const submit = async (e?: FormEvent, given?: string) => {
    e?.preventDefault();
    if (inflight.current) return;
    const c = normalizeCode(given ?? code);
    if (!c) {
      setErr(IP_GUARD_MSG.codeFormat);
      input.current?.focus();
      return;
    }
    inflight.current = true;
    setBusy(true);
    setErr(undefined);
    setNote(undefined);
    try {
      const out = await actions.submit(c);
      finish(() => resolve(out));
    } catch (x) {
      inflight.current = false;
      setBusy(false);
      if (x instanceof ApiError && x.code && RETRY_HERE.has(x.code)) {
        setErr(messageOf(x));
        setCode("");
        input.current?.focus();
        return;
      }
      // 5回失敗・無効・ほかの間違い（パスワードなど）は窓を閉じて、元の画面に文を出す
      finish(() => reject(x));
    }
  };

  /** 打った・入った値。6桁そろったら自動で確認（変換中は待つ） */
  const typed = (v: string, composing = false) => {
    setCode(v);
    if (!composing && normalizeCode(v)) void submit(undefined, v);
  };

  /** 欄に貼り付けた：文ごとでも6桁だけ拾う */
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const c = codeFromText(e.clipboardData.getData("text"));
    if (!c) return; // 拾えなければふつうの貼り付けに任せる
    e.preventDefault();
    setErr(undefined);
    typed(c);
  };

  /** 「貼り付け」ボタン：コピーしてきた文から6桁を拾って入れ、そのまま確認 */
  const paste = async () => {
    setErr(undefined);
    setNote(undefined);
    let text = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      setErr(IP_GUARD_MSG.codePasteDenied);
      input.current?.focus();
      return;
    }
    const c = codeFromText(text);
    if (!c) {
      setErr(IP_GUARD_MSG.codePasteNone);
      input.current?.focus();
      return;
    }
    typed(c);
  };

  const resend = async () => {
    setBusy(true);
    setErr(undefined);
    setNote(undefined);
    try {
      const r = await actions.resend();
      setSentTo(r.sentTo);
      setNote(IP_GUARD_MSG.codeResent);
      setCode("");
      input.current?.focus();
    } catch (x) {
      if (x instanceof ApiError && (x.code === "AUTH_CODE_LOCKED" || x.code === "AUTH_CODE_INVALID")) {
        finish(() => reject(x));
        return;
      }
      setErr(messageOf(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dim" data-testid="code-dialog">
      <form className="dlg codedlg" role="dialog" aria-modal="true" aria-labelledby="gb-code-h" onSubmit={(e) => void submit(e)} onKeyDown={(e) => e.key === "Escape" && !busy && cancel()} noValidate>
        <div className="dh" id="gb-code-h">
          {IP_GUARD_MSG.codeTitle}
          <Help testId="help-code" text="確認コードの有効期限は10分です。届かない場合は迷惑メールのフォルダもご確認ください。" />
        </div>
        <div className="db">
          <p style={{ margin: 0 }}>{IP_GUARD_MSG.codeSent(sentTo)}</p>
          <Field id="gb-code" label={IP_GUARD_MSG.codeLabel} className={canPaste ? "haspaste" : ""}>
            <input
              id="gb-code"
              ref={input}
              className="inp num codeinp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={12}
              value={code}
              onChange={(e) => typed(e.target.value, (e.nativeEvent as InputEvent).isComposing === true)}
              onCompositionEnd={(e) => typed(e.currentTarget.value)}
              onPaste={onPaste}
              onKeyDown={guardComposingEnter}
            />
            {canPaste ? (
              <button type="button" className="btn tonal fpaste" onClick={() => void paste()} disabled={busy} data-testid="code-paste">
                {IP_GUARD_MSG.codePaste}
              </button>
            ) : null}
          </Field>
          <ErrorText>{err}</ErrorText>
          {note ? (
            <p className="hint" role="status" style={{ margin: 0 }}>
              {note}
            </p>
          ) : null}
        </div>
        <div className="df">
          <button type="button" className="btn text" onClick={cancel} disabled={busy}>
            {IP_GUARD_MSG.codeCancel}
          </button>
          <span className="sp" />
          <button type="button" className="btn" onClick={() => void resend()} disabled={busy}>
            {IP_GUARD_MSG.codeResend}
          </button>
          <button type="submit" className="btn primary" disabled={busy} data-testid="code-submit">
            {IP_GUARD_MSG.codeSubmit}
          </button>
        </div>
      </form>
    </div>
  );
}
